import React, { useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import { useTheme } from '@mui/material/styles';
import { useGlobalContext } from '../contexts/GlobalContext';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import ImageLayer from 'ol/layer/Image';
import ImageStatic from 'ol/source/ImageStatic';
import Overlay from 'ol/Overlay';
import { fromLonLat, toLonLat } from 'ol/proj';
import { toStringHDMS } from 'ol/coordinate';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import LineString from 'ol/geom/LineString';
import Circle from 'ol/geom/Circle';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import { getMapLabel, getMapLabelOffset } from './mapLabels';
import EntityProperties from './EntityProperties';
import Icon from 'ol/style/Icon';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import CircleStyle from 'ol/style/Circle';
import Translate from 'ol/interaction/Translate';
import {
  DataGrid,
  GridActionsCellItem,
  gridClasses,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import {
  IconButton,
  Tooltip,
  Box,
  Button,
  Chip,
  Divider,
  Slider,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  getMapData,
  createMapData,
  updateMapData,
  deleteMapData,
  getEntityModels,
  getMonitorGroups,
} from '../services/api';
const MapViewer = ({ map, onBack }) => {
  const { pendingPinHighlight, clearPendingPinHighlight } = useGlobalContext();
  const mapRef = useRef(null);
  const olMapRef = useRef(null);
  const vectorSourceRef = useRef(null);
  const vectorLayerRef = useRef(null); // Store vector layer reference for style updates
  const popupRef = useRef(null);
  const popupOverlayRef = useRef(null);
  const translateInteractionRef = useRef(null);
  const mapDataRef = useRef([]); // Ref to always access current mapData
  const imageExtentRef = useRef(null); // Store image extent for pixel conversion
  const imageSizeRef = useRef(null); // Store image size for pixel conversion
  const isEditModeRef = useRef(false); // Ref for edit mode to avoid stale closures in event handlers
  const selectedFeatureIdRef = useRef(null); // Ref for selected feature ID to use in style function
  const [mapData, setMapData] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showDataForm, setShowDataForm] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [popupContent, setPopupContent] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingCoordinates, setPendingCoordinates] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shape_type: 'point',
    custom_pin_icon_id: null,
    geometry_data: {},
    style_data: { scale: 0.1, rotation: 0 },
  });
  const [entityModel, setEntityModel] = useState(null);
  const [entityValues, setEntityValues] = useState({});
  const [entityModels, setEntityModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [popupFeature, setPopupFeature] = useState(null); // Feature being shown in popup
  const [tempRotation, setTempRotation] = useState(0); // Temporary rotation value for slider
  const [tempScale, setTempScale] = useState(0.1); // Temporary scale/zoom value for slider
  const [monitorGroups, setMonitorGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const selectedGroupIdRef = useRef(null);
  const groupLinesSourceRef = useRef(null);
  const groupLinesLayerRef = useRef(null);

  // Define getDataGridColumns function BEFORE it's used
  const getDataGridColumns = () => [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
      type: 'number',
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      editable: false,
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      width: 120,
      editable: false,
      valueGetter: (value, row) => {
        return row?.custom_pin_icon?.vendor_name || 'N/A';
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 200,
      editable: false,
    },
    {
      field: 'shape_type',
      headerName: 'Shape Type',
      width: 120,
      editable: false,
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      width: 120,
      editable: false,
      valueFormatter: value => {
        return value?.username || value?.email || 'Unknown';
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      editable: false,
      type: 'dateTime',
      valueFormatter: value => {
        return value ? new Date(value).toLocaleString() : '';
      },
    },
    {
      field: 'updated_at',
      headerName: 'Updated',
      width: 120,
      editable: false,
      type: 'dateTime',
      valueFormatter: value => {
        return value ? new Date(value).toLocaleString() : '';
      },
    },
    {
      field: 'entity',
      headerName: 'Entity',
      width: 180,
      editable: false,
      valueGetter: (value, row) => row?.entity?.entity_model_name || '',
      renderCell: params => {
        const entity = params.row?.entity;
        if (!entity) return '—';
        const values = entity.values || {};
        const summary = Object.entries(values)
          .slice(0, 3)
          .map(([key, val]) => `${key}: ${val}`)
          .join(', ');
        return (
          <Tooltip title={summary || entity.entity_model_name}>
            <span>{entity.entity_model_name}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'monitor_group',
      headerName: 'Monitor Group',
      width: 160,
      editable: false,
      valueGetter: (value, row) => row?.entity?.monitor_group?.name || '',
      renderCell: params => {
        const group = params.row?.entity?.monitor_group;
        if (!group) return '—';
        return (
          <Tooltip
            title={`Role: ${group.role}${group.assigned_star ? ` · Star: ${group.assigned_star.name}` : ''}`}
          >
            <Chip
              label={group.name}
              size="small"
              variant="outlined"
              sx={{
                borderColor: '#22c55e',
                color: '#16a34a',
                fontWeight: 500,
                cursor: 'pointer',
              }}
              onClick={e => {
                e.stopPropagation();
                // Find the full group data from monitorGroups state
                const fullGroup = monitorGroups.find(g => g.id === group.id);
                if (fullGroup) {
                  setSelectedGroup(fullGroup);
                  setSelectedFeature(null);
                }
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEditData(row)}
          color="inherit"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteData(row)}
          color="inherit"
        />,
      ],
    },
  ];

  // Load initial column visibility from localStorage for uncontrolled mode
  const initialColumnVisibilityModel = React.useMemo(() => {
    const saved = localStorage.getItem(`mapDataGrid_columns_${map.id}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error(
          'Failed to parse column visibility from localStorage:',
          e
        );
      }
    }
    // Default: Description visible, Shape Type hidden
    return { shape_type: false };
  }, [map.id]);

  const theme = useTheme();

  // Helper function: Convert pixel coordinates (from top-left of image) to map coordinates
  const pixelToMapCoords = (pixelX, pixelY) => {
    if (!imageExtentRef.current || !imageSizeRef.current) {
      console.error('Image extent or size not available');
      return [0, 0];
    }

    const [minX, minY, maxX, maxY] = imageExtentRef.current;
    const [imageWidth, imageHeight] = imageSizeRef.current;

    // Calculate position as fraction of image size
    const xFraction = pixelX / imageWidth;
    const yFraction = 1 - pixelY / imageHeight; // Flip Y (image: top=0, map: bottom=0)

    // Convert to map coordinates
    const mapX = minX + xFraction * (maxX - minX);
    const mapY = minY + yFraction * (maxY - minY);

    return [mapX, mapY];
  };

  // Helper function: Convert map coordinates to pixel coordinates (relative to image)
  const mapCoordsToPixel = (mapX, mapY) => {
    if (!imageExtentRef.current || !imageSizeRef.current) {
      console.error('Image extent or size not available');
      return [0, 0];
    }

    const [minX, minY, maxX, maxY] = imageExtentRef.current;
    const [imageWidth, imageHeight] = imageSizeRef.current;

    // Calculate position as fraction of extent
    const xFraction = (mapX - minX) / (maxX - minX);
    const yFraction = (mapY - minY) / (maxY - minY);

    // Convert to pixel coordinates
    const pixelX = xFraction * imageWidth;
    const pixelY = (1 - yFraction) * imageHeight; // Flip Y back

    return [pixelX, pixelY];
  };

  useEffect(() => {
    if (!showDataForm) return;
    const orgId = map?.organization?.id ?? map?.organization;
    getEntityModels()
      .then(data => {
        const list = data?.results || data || [];
        setEntityModels(
          orgId
            ? list.filter(model => Number(model.organization) === Number(orgId))
            : list
        );
      })
      .catch(err => console.error('Failed to load entity models:', err));
  }, [showDataForm, map]);

  useEffect(() => {
    if (map) {
      initializeMap();
      loadMapData();
    }

    return () => {
      if (olMapRef.current) {
        olMapRef.current.setTarget(null);
      }
      setShowPopup(false);
    };
  }, [map]);

  // Keep mapDataRef in sync with mapData state
  useEffect(() => {
    mapDataRef.current = mapData;
    console.log('🔄 mapDataRef updated:', mapData.length, 'items');
  }, [mapData]);

  // Handle pending pin highlight from sidebar navigation (via global context)
  useEffect(() => {
    // Check if there's a pending highlight for this map
    if (!pendingPinHighlight || pendingPinHighlight.mapId !== map.id) return;

    // Wait for vector source to have features rendered
    if (
      !vectorSourceRef.current ||
      vectorSourceRef.current.getFeatures().length === 0
    )
      return;

    const { pinData } = pendingPinHighlight;

    if (pinData) {
      console.log('🎯 Highlighting feature from sidebar:', pinData);

      // Set selected feature directly (same as DataGrid click)
      setSelectedFeature(pinData);
      setTempRotation(pinData.style_data?.rotation || 0);
      setTempScale(pinData.style_data?.scale || 0.1);

      // Pan to the feature on the map
      const features = vectorSourceRef.current.getFeatures();
      const feature = features.find(f => f.get('id') === pinData.id);
      if (feature && olMapRef.current) {
        const geometry = feature.getGeometry();
        const coordinates = geometry.getCoordinates();
        olMapRef.current.getView().animate({
          center: coordinates,
          duration: 500,
        });
      }

      // Clear the pending highlight so it doesn't trigger again
      clearPendingPinHighlight();
    }
  }, [pendingPinHighlight, map.id, clearPendingPinHighlight]);

  // Keep isEditModeRef in sync with isEditMode state
  useEffect(() => {
    isEditModeRef.current = isEditMode;
    console.log('🔧 isEditModeRef updated:', isEditMode);
  }, [isEditMode]);

  // Keep selectedFeatureIdRef in sync and trigger redraw when selection changes
  useEffect(() => {
    const newId = selectedFeature?.id || null;
    selectedFeatureIdRef.current = newId;

    // Trigger redraw of the vector layer to update highlighting
    if (vectorSourceRef.current) {
      vectorSourceRef.current.changed();
    }

    // Pan to the selected feature if it exists
    if (selectedFeature && olMapRef.current && vectorSourceRef.current) {
      const features = vectorSourceRef.current.getFeatures();
      const feature = features.find(f => f.get('id') === selectedFeature.id);
      if (feature) {
        const geometry = feature.getGeometry();
        const coordinates = geometry.getCoordinates();
        olMapRef.current.getView().animate({
          center: coordinates,
          duration: 500,
        });
      }
    }
  }, [selectedFeature]);

  // Keep selectedGroupIdRef in sync and trigger redraw when group selection changes
  useEffect(() => {
    selectedGroupIdRef.current = selectedGroup?.id || null;
    if (groupLinesSourceRef.current) {
      groupLinesSourceRef.current.changed();
    }
  }, [selectedGroup]);

  // Keep exactly one click listener, and remove the same function we registered.
  useEffect(() => {
    const olMap = olMapRef.current;
    if (!olMap) return;
    olMap.on('click', handleMapClick);
    return () => olMap.un('click', handleMapClick);
  }, [isEditMode, mapData, map]);

  // Manage translate interaction for dragging features in edit mode
  useEffect(() => {
    if (!olMapRef.current || !vectorSourceRef.current) return;

    // Remove existing translate interaction if it exists
    if (translateInteractionRef.current) {
      olMapRef.current.removeInteraction(translateInteractionRef.current);
      translateInteractionRef.current = null;
    }

    // Add translate interaction only in edit mode
    if (isEditMode) {
      const translateInteraction = new Translate({
        layers: layer => layer.getSource() === vectorSourceRef.current,
        hitTolerance: getFeatureHitTolerance(),
      });

      console.log('🔧 Translate interaction created and configured');

      // Handle when a feature is finished being dragged
      translateInteraction.on('translateend', async event => {
        console.log('🎯 translateend event fired!', event);
        const features = event.features.getArray();
        console.log('📍 Features being dragged:', features.length);
        if (features.length > 0) {
          const feature = features[0];
          const dataId = feature.get('id');
          console.log('🆔 Feature ID:', dataId);

          if (dataId) {
            // Get the feature's new position
            const geometry = feature.getGeometry();
            const coordinates = geometry.getCoordinates();
            const lonLatCoordinates = toLonLat(coordinates);
            console.log('📌 New coordinates:', lonLatCoordinates);

            // Find the corresponding mapData item using the ref for current data
            const currentMapData = mapDataRef.current;
            console.log('📊 Current mapData:', currentMapData);

            // Extract results array from paginated response
            const mapDataArray = currentMapData?.results || [];
            console.log('📊 mapDataArray:', mapDataArray.length, 'items');

            const mapDataItem = mapDataArray.find(item => item.id === dataId);
            console.log('🔍 Found mapDataItem:', mapDataItem);

            if (mapDataItem) {
              try {
                // Get the feature's new position in pixel coordinates
                const pixelCoordinates = mapCoordsToPixel(
                  coordinates[0],
                  coordinates[1]
                );
                console.log(
                  '📌 New pixel coordinates:',
                  pixelCoordinates,
                  'from map coords:',
                  coordinates
                );

                // Update the geometry_data with new pixel coordinates
                const updatedData = {
                  ...mapDataItem,
                  geometry_data: {
                    ...mapDataItem.geometry_data,
                    pixel_coordinates: pixelCoordinates, // Store pixel coordinates
                    coordinates: lonLatCoordinates, // Keep lon/lat for backwards compatibility
                  },
                  map: map.id, // Include map ID for the API
                };

                // Remove read-only fields
                delete updatedData.id;
                delete updatedData.created_at;
                delete updatedData.updated_at;
                delete updatedData.created_by;
                delete updatedData.custom_pin_icon;
                delete updatedData.entity;

                // Update via API
                await updateMapData(dataId, updatedData);

                // Reload map data to reflect changes
                await loadMapData();

                console.log('Feature moved successfully:', {
                  id: dataId,
                  newCoordinates: lonLatCoordinates,
                });
              } catch (error) {
                console.error('Error updating feature position:', error);
                alert('Error updating feature position. Please try again.');

                // Reload to restore original position
                await loadMapData();
              }
            }
          }
        }
      });

      olMapRef.current.addInteraction(translateInteraction);
      translateInteractionRef.current = translateInteraction;
    }

    // Cleanup function
    return () => {
      if (
        translateInteractionRef.current &&
        olMapRef.current &&
        olMapRef.current.getInteractions
      ) {
        olMapRef.current.removeInteraction(translateInteractionRef.current);
        translateInteractionRef.current = null;
      }
    };
  }, [isEditMode, mapData, map]); // Re-run when edit mode, map data, or map changes

  const initializeMap = () => {
    const vectorSource = new VectorSource();
    vectorSourceRef.current = vectorSource;

    // Get meters per pixel from map config - used for real-world icon scaling
    const metersPerPixel = map.meters_per_pixel || 0.042;

    // Base resolution for real-world scaling
    // This represents the ground distance per pixel at which the icon scale is "1:1"
    const baseResolution = metersPerPixel;

    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: (feature, resolution) => {
        const styleData = feature.get('style_data') || {};
        const featureId = feature.get('id');
        const isSelected = featureId === selectedFeatureIdRef.current;

        // Create icon source with anchor indicator overlay
        let iconSrc;
        if (styleData.iconSrc) {
          // For custom icons, use as-is
          iconSrc = styleData.iconSrc;
        } else {
          // For default pin icon, add anchor indicator in SVG
          const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${styleData.color || '#ff0000'}"/>
            <polygon points="0,0 6,0 0,6" fill="#00ff00" stroke="#000000" stroke-width="0.5" opacity="0.8"/>
          </svg>`;
          iconSrc =
            'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
        }

        const featureScale = parseFloat(styleData.scale || '0.06');
        const rotation =
          parseFloat(styleData.rotation || '0') * (Math.PI / 180); // Convert degrees to radians

        // Calculate scale to maintain constant REAL-WORLD size
        // Resolution = meters per pixel
        // When zoomed OUT: resolution is HIGH (more meters per pixel) -> need SMALLER icon in pixels
        // When zoomed IN: resolution is LOW (fewer meters per pixel) -> need LARGER icon in pixels
        // Formula: scale inversely with resolution to maintain constant ground coverage
        const zoomCompensation = baseResolution / resolution;
        const compensatedScale = featureScale * zoomCompensation;

        // Create background SVG - match icon dimensions (icons are 1024x1024)
        const iconSize = 1024;
        const padding = 60; // Padding around icon
        const bgSize = iconSize + padding * 2;
        const cornerRadius = 40;

        // Background starts at (-padding, -padding) so it extends before the icon's (0,0) anchor
        const backgroundSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${bgSize}" height="${bgSize}" viewBox="${-padding} ${-padding} ${bgSize} ${bgSize}">
          <rect x="${-padding}" y="${-padding}" width="${bgSize}" height="${bgSize}" 
            rx="${cornerRadius}" ry="${cornerRadius}"
            fill="rgba(255, 250, 150, 0.9)" 
            stroke="rgba(200, 150, 0, 0.9)" 
            stroke-width="12"/>
        </svg>`;
        const backgroundSrc =
          'data:image/svg+xml;charset=utf-8,' +
          encodeURIComponent(backgroundSvg);

        // Create anchor indicator triangle SVG - sized relative to icon
        const triangleSize = 150; // Visible size relative to 1024px icon
        const anchorTriangleSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${triangleSize}" height="${triangleSize}" viewBox="0 0 ${triangleSize} ${triangleSize}">
          <polygon points="0,0 ${triangleSize},0 0,${triangleSize}" fill="#00ff00" stroke="#004400" stroke-width="6"/>
        </svg>`;
        const anchorTriangleSrc =
          'data:image/svg+xml;charset=utf-8,' +
          encodeURIComponent(anchorTriangleSvg);

        // Create selection highlight ring SVG for selected features
        const highlightSize = iconSize + padding * 4; // Larger than background
        const highlightSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${highlightSize}" height="${highlightSize}" viewBox="${-padding * 2} ${-padding * 2} ${highlightSize} ${highlightSize}">
          <rect x="${-padding * 2}" y="${-padding * 2}" width="${highlightSize}" height="${highlightSize}" 
            rx="${cornerRadius + 20}" ry="${cornerRadius + 20}"
            fill="none" 
            stroke="rgba(0, 120, 255, 0.8)" 
            stroke-width="24"
            stroke-dasharray="80 40"/>
        </svg>`;
        const highlightSrc =
          'data:image/svg+xml;charset=utf-8,' +
          encodeURIComponent(highlightSvg);

        // Build styles array
        const styles = [];

        // Add selection highlight ring first (behind everything) if selected
        if (isSelected) {
          styles.push(
            new Style({
              image: new Icon({
                anchor: [0, 0],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction',
                src: highlightSrc,
                scale: compensatedScale,
                rotation: rotation,
              }),
            })
          );
        }

        // Background layer - same anchor and scale as icon
        styles.push(
          new Style({
            image: new Icon({
              anchor: [0, 0], // Same anchor as icon
              anchorXUnits: 'fraction',
              anchorYUnits: 'fraction',
              src: backgroundSrc,
              scale: compensatedScale, // Same scale as icon
              rotation: rotation,
            }),
          })
        );

        // Main icon layer
        styles.push(
          new Style({
            image: new Icon({
              anchor: [0, 0],
              anchorXUnits: 'fraction',
              anchorYUnits: 'fraction',
              src: iconSrc,
              scale: compensatedScale,
              rotation: rotation,
            }),
            fill: new Fill({
              color: styleData.fillColor || 'rgba(255, 0, 0, 0.3)',
            }),
            stroke: new Stroke({
              color: styleData.strokeColor || '#ff0000',
              width: parseInt(styleData.strokeWidth || '2'),
            }),
          })
        );

        // Anchor indicator triangle
        styles.push(
          new Style({
            image: new Icon({
              anchor: [0, 0],
              anchorXUnits: 'fraction',
              anchorYUnits: 'fraction',
              src: anchorTriangleSrc,
              scale: compensatedScale, // Same scale as icon
              rotation: rotation,
            }),
          })
        );

        const label = feature.get('mapLabel');
        if (label && feature.get('shape_type') === 'point') {
          const image = styles[isSelected ? 2 : 1].getImage();
          const [width, height] = image.getSize() || [iconSize, iconSize];
          styles.push(
            new Style({
              text: new Text({
                text: label,
                font: '600 14px Roboto, sans-serif',
                textAlign: 'center',
                textBaseline: 'bottom',
                ...getMapLabelOffset(width, height, compensatedScale, rotation),
                fill: new Fill({ color: '#111827' }),
                stroke: new Stroke({ color: '#ffffff', width: 3 }),
              }),
            })
          );
        }
        // Keep every part of the selected icon above overlapping features so
        // OpenLayers hit detection and dragging target the visible selection.
        styles.forEach(style => style.setZIndex(isSelected ? 1 : 0));
        return styles;
      },
    });

    vectorLayerRef.current = vectorLayer;

    const layers = [];
    let mapCenter;

    // Check if the map has a background image
    if (!map.background_image_url) {
      setError('No background image available for this map.');
      setLoading(false);
      return;
    }

    // Use map-specific configuration with default coordinates if not provided
    const centerLat = map.center_latitude || 40.770826133692374; // Default latitude
    const centerLon = map.center_longitude || -111.837070913926; // Default longitude

    const centerXY = fromLonLat([centerLon, centerLat]);
    mapCenter = centerXY;

    const imageSizePx = [
      map.image_size_width || 2048,
      map.image_size_height || 1462,
    ];

    const [pxW, pxH] = imageSizePx;
    const groundWidthMeters = pxW * metersPerPixel;
    const groundHeightMeters = pxH * metersPerPixel;

    const halfW = groundWidthMeters / 2;
    const halfH = groundHeightMeters / 2;
    const extent = [
      centerXY[0] - halfW,
      centerXY[1] - halfH,
      centerXY[0] + halfW,
      centerXY[1] + halfH,
    ];

    // Store extent and image size for pixel coordinate conversion
    imageExtentRef.current = extent;
    imageSizeRef.current = imageSizePx;
    console.log('📐 Image extent stored:', extent);
    console.log('📏 Image size stored:', imageSizePx);

    const imageSource = new ImageStatic({
      url: map.background_image_url,
      imageExtent: extent,
      projection: 'EPSG:3857',
    });

    // Add error handling for image loading
    imageSource.on('imageloaderror', () => {
      setError('Failed to load background image for this map.');
      setLoading(false);
    });

    imageSource.on('imageloadend', () => {
      setError(null);
      setLoading(false);
    });

    const mapImageLayer = new ImageLayer({
      source: imageSource,
    });

    layers.push(mapImageLayer);

    // Monitor group connection lines layer
    const groupLinesSource = new VectorSource();
    groupLinesSourceRef.current = groupLinesSource;
    const groupLinesLayer = new VectorLayer({
      source: groupLinesSource,
      style: feature => {
        const isSelected =
          feature.get('groupId') === selectedGroupIdRef.current;
        return new Style({
          stroke: new Stroke({
            color: isSelected ? '#16a34a' : '#22c55e',
            width: isSelected ? 4 : 2.5,
            lineDash: [12, 8],
          }),
        });
      },
    });
    groupLinesLayerRef.current = groupLinesLayer;
    layers.push(groupLinesLayer);

    layers.push(vectorLayer);

    // Use data-driven zoom level with fallback to default
    const defaultZoom = map.default_zoom || 22; // Use map's default_zoom or fallback to 22

    const olMap = new Map({
      target: mapRef.current,
      layers: layers,
      view: new View({
        center: mapCenter, // Use the center from the selected image
        zoom: defaultZoom, // Data-driven zoom level
      }),
    });

    // Create popup overlay
    const popup = new Overlay({
      element: popupRef.current,
      positioning: 'bottom-center',
      stopEvent: false,
      offset: [0, -50],
    });
    olMap.addOverlay(popup);
    popupOverlayRef.current = popup;

    // Add pointer move handler for cursor changes
    olMap.on('pointermove', event => {
      const hitTolerance = getFeatureHitTolerance();

      const feature = olMap.forEachFeatureAtPixel(event.pixel, f => f, {
        hitTolerance,
      });

      if (isEditModeRef.current) {
        olMap.getTargetElement().style.cursor = feature
          ? 'pointer'
          : 'crosshair';
      } else {
        // In view mode, show pointer cursor on group lines and features
        olMap.getTargetElement().style.cursor = feature ? 'pointer' : 'default';
      }
    });

    olMapRef.current = olMap;
  };

  // Update a specific feature's visual style (rotation and scale) in real-time
  const updateFeatureStyle = (featureId, rotation, scale) => {
    if (!vectorSourceRef.current) return;

    const features = vectorSourceRef.current.getFeatures();
    const feature = features.find(f => f.get('id') === featureId);

    if (!feature) return;

    // Update the feature's style_data property directly (like edit mode does)
    const styleData = feature.get('style_data') || {};
    feature.set('style_data', {
      ...styleData,
      rotation: rotation,
      scale: scale,
    });

    // Force re-render by notifying the vector source of changes (like edit mode does)
    vectorSourceRef.current.changed();

    console.log('🔄 Updated feature style:', { rotation, scale });
  };

  const loadMapData = async () => {
    try {
      const orgId = map?.organization?.id ?? map?.organization;
      const [data, groups] = await Promise.all([
        getMapData(map.id),
        orgId ? getMonitorGroups(orgId) : Promise.resolve([]),
      ]);
      // Data loaded successfully
      setMapData(data);
      setMonitorGroups(groups);
      setSelectedFeature(current =>
        current
          ? (data.results || []).find(item => item.id === current.id) || null
          : null
      );
      mapDataRef.current = data; // Keep ref in sync
      renderMapData(data);
      renderMonitorGroupLines(groups, data);
    } catch (error) {
      console.error('Error loading map data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderMapData = data => {
    if (!vectorSourceRef.current) return;

    vectorSourceRef.current.clear();

    // Defensive: use data.results or []
    const items = Array.isArray(data?.results) ? data.results : [];
    items.forEach(item => {
      const feature = createFeatureFromData(item);
      if (feature) {
        vectorSourceRef.current.addFeature(feature);
      }
    });
  };

  const renderMonitorGroupLines = (groups, mapDataItems) => {
    if (!groupLinesSourceRef.current) return;
    groupLinesSourceRef.current.clear();

    const items = Array.isArray(mapDataItems?.results)
      ? mapDataItems.results
      : [];

    // Build a lookup from entity ID to map data item
    const entityIdToMapData = {};
    items.forEach(item => {
      if (item.entity?.id) {
        entityIdToMapData[item.entity.id] = item;
      }
    });

    groups.forEach(group => {
      // Collect all member entity IDs in this group (master + secondaries)
      const memberEntityIds = [];
      if (group.master?.id) memberEntityIds.push(group.master.id);
      if (group.secondaries) {
        group.secondaries.forEach(s => {
          if (s.id) memberEntityIds.push(s.id);
        });
      }

      // Find which members are on this map and have coordinates
      const coords = [];
      memberEntityIds.forEach(entityId => {
        const mapItem = entityIdToMapData[entityId];
        if (!mapItem) return;
        const geom = mapItem.geometry_data;
        if (geom?.pixel_coordinates) {
          const mapCoords = pixelToMapCoords(
            geom.pixel_coordinates[0],
            geom.pixel_coordinates[1]
          );
          coords.push(mapCoords);
        } else if (geom?.coordinates) {
          coords.push(fromLonLat(geom.coordinates));
        }
      });

      // Need at least 2 points to draw a line
      if (coords.length < 2) return;

      // Collect member names that are on this map for the detail panel
      const membersOnMap = [];
      memberEntityIds.forEach(entityId => {
        const mapItem = entityIdToMapData[entityId];
        if (!mapItem) return;
        const isMaster = group.master?.id === entityId;
        membersOnMap.push({
          entityId,
          name: mapItem.name,
          role: isMaster ? 'master' : 'secondary',
          mapDataId: mapItem.id,
        });
      });

      const lineFeature = new Feature(new LineString(coords));
      lineFeature.set('groupName', group.name);
      lineFeature.set('groupId', group.id);
      lineFeature.set('groupData', group);
      lineFeature.set('membersOnMap', membersOnMap);
      lineFeature.set('isGroupLine', true);
      groupLinesSourceRef.current.addFeature(lineFeature);
    });
  };

  const createFeatureFromData = data => {
    let geometry;
    const coords = data.geometry_data;

    switch (data.shape_type) {
      case 'point':
        if (coords.coordinates) {
          // Check if we have pixel coordinates or lon/lat
          if (coords.pixel_coordinates) {
            // New format: pixel coordinates relative to image
            const [pixelX, pixelY] = coords.pixel_coordinates;
            const mapCoords = pixelToMapCoords(pixelX, pixelY);
            geometry = new Point(mapCoords);
            console.log(
              `📍 Point from pixels [${pixelX}, ${pixelY}] → map coords:`,
              mapCoords
            );
          } else {
            // Legacy format: lon/lat coordinates
            geometry = new Point(fromLonLat(coords.coordinates));
            console.log('📍 Point from lon/lat (legacy):', coords.coordinates);
          }
        }
        break;
      case 'polygon':
        if (coords.coordinates) {
          // Handle both proper polygon format and incorrect point format
          if (
            Array.isArray(coords.coordinates[0]) &&
            Array.isArray(coords.coordinates[0][0])
          ) {
            // Proper polygon format: [[[x1,y1], [x2,y2], ...]]
            geometry = new Polygon(
              coords.coordinates.map(ring =>
                ring.map(coord => fromLonLat(coord))
              )
            );
          } else if (
            Array.isArray(coords.coordinates[0]) &&
            typeof coords.coordinates[0][0] === 'number'
          ) {
            // Ring array format: [[x1,y1], [x2,y2], ...]
            geometry = new Polygon([
              coords.coordinates.map(coord => fromLonLat(coord)),
            ]);
          } else if (
            typeof coords.coordinates[0] === 'number' &&
            coords.coordinates.length === 2
          ) {
            // Incorrect point format stored as polygon - convert to point
            console.warn(
              `Polygon data ${data.id} has point coordinates, treating as point`
            );
            geometry = new Point(fromLonLat(coords.coordinates));
          }
        }
        break;
      case 'line':
        if (coords.coordinates) {
          geometry = new LineString(
            coords.coordinates.map(coord => fromLonLat(coord))
          );
        }
        break;
      case 'circle':
        if (coords.center && coords.radius) {
          geometry = new Circle(fromLonLat(coords.center), coords.radius);
        }
        break;
      default:
        return null;
    }

    if (geometry) {
      const feature = new Feature(geometry);

      // Prepare style data with custom icon if available
      let styleData = data.style_data || {};
      if (data.shape_type === 'point' && data.custom_pin_icon?.icon_image_url) {
        styleData = {
          ...styleData,
          iconSrc: data.custom_pin_icon.icon_image_url,
        };
      }

      feature.setProperties({
        id: data.id,
        name: data.name,
        description: data.description,
        shape_type: data.shape_type,
        style_data: styleData,
        custom_pin_icon: data.custom_pin_icon,
        mapLabel: getMapLabel(
          data.custom_pin_icon?.entity_model?.fields || [],
          data.entity?.values || {}
        ),
      });
      return feature;
    }

    return null;
  };

  const getFeatureHitTolerance = () => {
    const features = vectorSourceRef.current?.getFeatures() || [];
    const maxScale = features.reduce(
      (maximum, feature) =>
        Math.max(maximum, feature.get('style_data')?.scale || 0.1),
      0.1
    );
    return Math.max(20, maxScale * 100);
  };

  const handleMapClick = event => {
    const coordinate = event.coordinate;
    const lonLatCoordinate = toLonLat(coordinate);
    const pixelCoordinate = mapCoordsToPixel(coordinate[0], coordinate[1]);

    // Console log the clicked coordinates
    console.log('🖱️ CLICK EVENT - Clicked coordinates:', {
      projected: coordinate,
      lonLat: lonLatCoordinate,
      pixel: pixelCoordinate,
      formatted: toStringHDMS(lonLatCoordinate),
    });

    // Debug the edit mode state
    console.log('🔍 Edit mode state (STATE):', isEditMode);
    console.log('🔍 Edit mode state (REF):', isEditModeRef.current);

    const hitTolerance = getFeatureHitTolerance();

    const features = olMapRef.current.getFeaturesAtPixel(event.pixel, {
      hitTolerance,
    });

    // If clicked on an existing feature, show the feature-info menu (both edit and view mode)
    if (features && features.length > 0) {
      const feature = features[0];

      // Check if it's a monitor group line
      if (feature.get('isGroupLine')) {
        const groupData = feature.get('groupData');
        const membersOnMap = feature.get('membersOnMap');
        setSelectedGroup({ ...groupData, membersOnMap });
        setSelectedFeature(null);
        return;
      }

      const dataId = feature.get('id');
      if (dataId) {
        // mapData is a paginated response, access results array
        // Use ref to avoid stale closure
        const mapDataArray = mapDataRef.current?.results || [];
        const mapDataItem = mapDataArray.find(item => item.id === dataId);

        if (!mapDataItem) {
          console.error('MapDataItem not found for ID:', dataId);
          return;
        }

        setSelectedFeature(mapDataItem);
        setSelectedGroup(null);
        setTempRotation(mapDataItem.style_data?.rotation || 0); // Initialize temp rotation
        setTempScale(mapDataItem.style_data?.scale || 0.1); // Initialize temp scale

        // Show the feature-info menu (top-right), not the popup modal
        return;
      }
    }

    // Clicked on empty area - only create new point if in edit mode
    if (isEditModeRef.current) {
      const hdms = toStringHDMS(lonLatCoordinate);
      const coordinates = {
        projected: coordinate,
        lonLat: lonLatCoordinate,
        pixel: pixelCoordinate,
        formatted: hdms,
      };

      setPendingCoordinates(coordinates);

      // Open the modal form with pixel coordinates for new point
      setFormData({
        name: '',
        description: '',
        shape_type: 'point',
        custom_pin_icon_id: null,
        geometry_data: {
          pixel_coordinates: pixelCoordinate, // Store pixel coordinates
          coordinates: coordinates.lonLat, // Keep lon/lat for backwards compatibility
        },
        style_data: { scale: 0.1, rotation: 0 },
      });
      setEntityModel(null);
      setEntityValues({});
      setEditingData(null);
      setShowDataForm(true);
      setShowPopup(false);
    }

    // In view mode, clicked on empty area - do nothing
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    setShowPopup(false);
    setPendingCoordinates(null);
    setSelectedFeature(null);
    setSelectedGroup(null);
  };

  const applyEntityModel = (model, { fillName = false } = {}) => {
    setEntityModel(model);
    setEntityValues({});
    setFormData(prev => ({
      ...prev,
      name:
        fillName && !prev.name.trim() ? model?.name || prev.name : prev.name,
      shape_type: 'point',
      custom_pin_icon_id: model?.pin_icon?.id ?? null,
    }));
  };

  const handleCreateDataAtCoordinates = () => {
    if (!pendingCoordinates) return;

    setFormData({
      name: '',
      description: '',
      shape_type: 'point',
      custom_pin_icon_id: null,
      geometry_data: {
        coordinates: pendingCoordinates.lonLat,
      },
      style_data: { scale: 0.1, rotation: 0 },
    });
    setEntityModel(null);
    setEntityValues({});
    setEditingData(null);
    setShowDataForm(true);
    setShowPopup(false);
  };

  const handleCreateData = () => {
    // Get the center of the current map view as default coordinates
    let defaultCoordinates = [0, 0];
    if (olMapRef.current) {
      const viewCenter = olMapRef.current.getView().getCenter();
      if (viewCenter) {
        defaultCoordinates = toLonLat(viewCenter);
      }
    }

    setFormData({
      name: '',
      description: '',
      shape_type: 'point',
      custom_pin_icon_id: null,
      geometry_data: {
        coordinates: defaultCoordinates,
      },
      style_data: { scale: 0.1, rotation: 0 },
    });
    setEntityModel(null);
    setEntityValues({});
    setEditingData(null);
    setShowDataForm(true);
  };

  const handleEditData = data => {
    // Ensure proper scale and rotation parsing - handle both string and numeric values
    const existingStyleData = data.style_data || {};
    const scale = existingStyleData.scale
      ? typeof existingStyleData.scale === 'string'
        ? parseFloat(existingStyleData.scale)
        : existingStyleData.scale
      : data.custom_pin_icon
        ? 0.1
        : 0.1; // Default scale for custom icons

    const rotation = existingStyleData.rotation
      ? typeof existingStyleData.rotation === 'string'
        ? parseInt(existingStyleData.rotation)
        : existingStyleData.rotation
      : 0; // Default rotation

    setFormData({
      name: data.name,
      description: data.description,
      shape_type: data.shape_type,
      custom_pin_icon_id: data.custom_pin_icon ? data.custom_pin_icon.id : null,
      geometry_data: data.geometry_data,
      style_data: {
        ...existingStyleData,
        scale: scale,
        rotation: rotation,
      },
    });
    setEntityModel(data.custom_pin_icon?.entity_model || null);
    setEntityValues(data.entity?.values || {});
    setEditingData(data);
    setShowDataForm(true);
    setSelectedFeature(null);
  };

  const handleDeleteData = async data => {
    if (window.confirm(`Are you sure you want to delete "${data.name}"?`)) {
      try {
        await deleteMapData(data.id);
        loadMapData();
        setSelectedFeature(null);
      } catch (error) {
        alert('Error deleting map data. Please try again.');
      }
    }
  };

  const handlePopupSliderChange = async () => {
    // Use either popupFeature or selectedFeature depending on which is active
    const featureToUpdate = popupFeature || selectedFeature;
    if (!featureToUpdate) return;

    try {
      const updatedData = {
        ...featureToUpdate,
        style_data: {
          ...featureToUpdate.style_data,
          rotation: tempRotation,
          scale: tempScale,
        },
        map: map.id,
      };

      // Remove read-only fields
      delete updatedData.id;
      delete updatedData.created_at;
      delete updatedData.updated_at;
      delete updatedData.created_by;
      delete updatedData.custom_pin_icon;
      delete updatedData.entity;

      await updateMapData(featureToUpdate.id, updatedData);
      await loadMapData();

      console.log('Feature updated successfully:', {
        id: featureToUpdate.id,
        rotation: tempRotation,
        scale: tempScale,
      });
    } catch (error) {
      console.error('Error updating feature from slider:', error);
      alert('Error updating feature. Please try again.');
    }
  };

  const handleFormSubmit = async e => {
    e.preventDefault();

    try {
      const submitData = {
        ...formData,
        map: map.id,
        entity: entityModel
          ? { entity_model_id: entityModel.id, values: entityValues }
          : null,
      };

      if (editingData) {
        await updateMapData(editingData.id, submitData);
      } else {
        await createMapData(submitData);
      }

      setShowDataForm(false);
      setPendingCoordinates(null);
      loadMapData();
    } catch (error) {
      alert('Error saving map data. Please try again.');
    }
  };

  return (
    <div className="map-viewer">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Box>
          <h2 style={{ margin: 0, color: 'inherit' }}>{map.name}</h2>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <span style={{ marginRight: '8px' }}>Mode:</span>
            <strong>{isEditMode ? 'Edit' : 'View'}</strong>
          </Box>
          <Button
            onClick={toggleEditMode}
            variant="contained"
            color={isEditMode ? 'error' : 'success'}
            size="medium"
            sx={{ fontWeight: 'bold' }}
          >
            {isEditMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
          </Button>
        </Box>
      </Box>

      {error && (
        <div
          className="error-message"
          style={{
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            padding: '15px',
            margin: '10px 0',
            borderRadius: '4px',
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#721c24',
          }}
        >
          ❌ Error: {error}
        </div>
      )}

      {isEditMode && !error && (
        <div
          className="edit-mode-banner"
          style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            padding: '10px',
            margin: '10px 0',
            borderRadius: '4px',
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#856404',
          }}
        >
          🖊️ Edit Mode Active - Drag icons to move them. If icons overlap,
          select one in Map Data Details below, then drag it on the map. Click
          empty space to add a new data point.
        </div>
      )}

      {!error && (
        <div style={{ position: 'relative' }}>
          <div className="map-container" style={{ position: 'relative' }}>
            <div
              ref={mapRef}
              className="map-display"
              style={{
                width: '100%',
                height: '600px',
                cursor: isEditMode ? 'crosshair' : 'default',
              }}
            >
              {loading && (
                <div className="map-loading">
                  <div className="spinner"></div>
                  <p>Loading map...</p>
                </div>
              )}
            </div>
          </div>

          {/* Popup overlay element */}
          <div
            ref={popupRef}
            className={`map-popup ${showPopup ? 'visible' : ''}`}
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `2px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              padding: '15px',
              minWidth: '250px',
              maxWidth: '350px',
              fontSize: '14px',
              boxShadow: theme.shadows[8],
              position: 'relative',
              opacity: showPopup ? 1 : 0,
              pointerEvents: showPopup ? 'auto' : 'none',
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            <button
              onClick={() => setShowPopup(false)}
              style={{
                position: 'absolute',
                top: '5px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#666',
              }}
            >
              ×
            </button>
            <div dangerouslySetInnerHTML={{ __html: popupContent }} />

            {/* Action Buttons */}
            {popupFeature && (
              <div style={{ marginTop: '15px', display: 'flex', gap: '8px' }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    handleEditData(popupFeature);
                    setShowPopup(false);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteData(popupFeature);
                    setShowPopup(false);
                  }}
                >
                  Delete
                </Button>
              </div>
            )}

            <div
              style={{
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderTop: '10px solid #333',
              }}
            />
          </div>

          {/* Top-right feature control menu - positioned absolutely in top-right */}
          {selectedFeature && (
            <div
              className="feature-info"
              style={{
                width: '300px',
                minWidth: 0,
                maxWidth: 'calc(100% - 2rem)',
                maxHeight: 'calc(600px - 2rem)',
                overflowY: 'auto',
                boxSizing: 'border-box',
              }}
            >
              <h3>{selectedFeature.name}</h3>
              <p>{selectedFeature.description}</p>

              {/* Rotation Slider */}
              <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <label style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    Rotation:
                  </label>
                  <span style={{ fontSize: '14px' }}>{tempRotation}°</span>
                </div>
                <Slider
                  value={tempRotation}
                  onChange={(e, value) => {
                    setTempRotation(value);
                    // Update visual immediately while dragging
                    updateFeatureStyle(selectedFeature.id, value, tempScale);
                  }}
                  onChangeCommitted={handlePopupSliderChange}
                  min={0}
                  max={360}
                  step={5}
                  valueLabelDisplay="auto"
                  size="small"
                  sx={{ width: '100%' }}
                />
              </div>

              {/* Scale/Zoom Slider */}
              <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <label style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    Zoom:
                  </label>
                  <span style={{ fontSize: '14px' }}>
                    {tempScale.toFixed(3)}
                  </span>
                </div>
                <Slider
                  value={tempScale}
                  onChange={(e, value) => {
                    setTempScale(value);
                    // Update visual immediately while dragging
                    updateFeatureStyle(selectedFeature.id, tempRotation, value);
                  }}
                  onChangeCommitted={handlePopupSliderChange}
                  min={0.01}
                  max={0.1}
                  step={0.005}
                  valueLabelDisplay="auto"
                  size="small"
                  sx={{ width: '100%' }}
                />
              </div>

              <EntityProperties
                entity={selectedFeature.entity}
                model={selectedFeature.custom_pin_icon?.entity_model}
              />

              <div className="feature-actions">
                <button onClick={() => handleEditData(selectedFeature)}>
                  Edit
                </button>
                <button onClick={() => handleDeleteData(selectedFeature)}>
                  Delete
                </button>
                <button onClick={() => setSelectedFeature(null)}>Close</button>
              </div>
            </div>
          )}

          {/* Monitor Group detail panel */}
          {selectedGroup && !selectedFeature && (
            <div
              className="feature-info"
              style={{
                width: '300px',
                minWidth: 0,
                maxWidth: 'calc(100% - 2rem)',
                maxHeight: 'calc(600px - 2rem)',
                overflowY: 'auto',
                boxSizing: 'border-box',
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {selectedGroup.name}
              </Typography>
              <Chip
                label="Monitor Group"
                size="small"
                sx={{
                  bgcolor: '#22c55e',
                  color: '#fff',
                  fontWeight: 600,
                  mb: 1.5,
                }}
              />

              {/* Master */}
              {selectedGroup.master && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 0.5 }}
                  >
                    Master
                  </Typography>
                  <Typography variant="body2">
                    {selectedGroup.master.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedGroup.master.entity_model_name}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              {/* Secondaries */}
              {selectedGroup.secondaries?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 0.5 }}
                  >
                    Secondaries ({selectedGroup.secondaries.length})
                  </Typography>
                  {selectedGroup.secondaries.map(s => (
                    <Box key={s.id} sx={{ mb: 0.5 }}>
                      <Typography variant="body2">{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.entity_model_name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              {/* Assigned Star */}
              {selectedGroup.assigned_star && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 0.5 }}
                  >
                    Assigned Star ⭐
                  </Typography>
                  <Typography variant="body2">
                    {selectedGroup.assigned_star.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedGroup.assigned_star.entity_model_name}
                  </Typography>
                </Box>
              )}

              {/* Custom field values */}
              {selectedGroup.values &&
                Object.keys(selectedGroup.values).length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 'bold', mb: 0.5 }}
                      >
                        Properties
                      </Typography>
                      {Object.entries(selectedGroup.values).map(
                        ([key, value]) => (
                          <Box
                            key={key}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.25,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {key}
                            </Typography>
                            <Typography variant="body2">
                              {String(value)}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  </>
                )}

              {/* Members on this map */}
              {selectedGroup.membersOnMap?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'bold', mb: 0.5 }}
                    >
                      On This Map ({selectedGroup.membersOnMap.length})
                    </Typography>
                    {selectedGroup.membersOnMap.map(m => (
                      <Box
                        key={m.entityId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {m.name}
                        </Typography>
                        <Chip
                          label={m.role}
                          size="small"
                          variant="outlined"
                          color={m.role === 'master' ? 'primary' : 'default'}
                        />
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              <div className="feature-actions">
                <button
                  onClick={() => {
                    const orgId =
                      selectedGroup.organization ??
                      map?.organization?.id ??
                      map?.organization;
                    window.open(
                      `/monitor-groups?organization=${orgId}&group=${selectedGroup.id}`,
                      '_blank'
                    );
                  }}
                >
                  Edit Group
                </button>
                <button onClick={() => setSelectedGroup(null)}>Close</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Map Data Grid */}
      <div className="mapdata-grid" style={{ marginTop: '2em' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1em',
          }}
        >
          <h2 style={{ margin: 0 }}>Map Data Details</h2>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedFeature && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => handleEditData(selectedFeature)}
              >
                Edit Selected
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreateData}
            >
              Add Entity
            </Button>
          </Box>
        </div>
        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={Array.isArray(mapData?.results) ? mapData.results : []}
            columns={getDataGridColumns()}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
              sorting: {
                sortModel: [{ field: 'created_at', sort: 'desc' }],
              },
              columns: {
                columnVisibilityModel: initialColumnVisibilityModel,
              },
            }}
            onColumnVisibilityModelChange={newModel => {
              localStorage.setItem(
                `mapDataGrid_columns_${map.id}`,
                JSON.stringify(newModel)
              );
            }}
            pageSizeOptions={[5, 10, 25]}
            disableRowSelectionOnClick
            onRowClick={params => {
              // Select the clicked row and highlight its feature on the map
              const clickedItem = params.row;
              setSelectedFeature(clickedItem);
              setSelectedGroup(null);
              setTempRotation(clickedItem.style_data?.rotation || 0);
              setTempScale(clickedItem.style_data?.scale || 0.1);
            }}
            sx={{
              // Highlight the selected row
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              [`& .MuiDataGrid-row[data-id="${selectedFeature?.id}"]`]: {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              },
            }}
          />
        </Box>
      </div>

      {showDataForm && (
        <div className="form-modal">
          <div className="form-card">
            <div className="form-header">
              <h3>{editingData ? 'Edit Map Data' : 'Add Entity'}</h3>
              <button
                onClick={() => setShowDataForm(false)}
                className="close-button"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleFormSubmit}>
              {pendingCoordinates && (
                <div className="form-group">
                  <label>Location:</label>
                  <div
                    style={{
                      backgroundColor: '#f8f9fa',
                      padding: '10px',
                      borderRadius: '4px',
                      border: '1px solid #dee2e6',
                      fontSize: '14px',
                    }}
                  >
                    <strong>Coordinates:</strong> {pendingCoordinates.formatted}
                    <br />
                    <strong>Lat/Lon:</strong>{' '}
                    {pendingCoordinates.lonLat[1].toFixed(6)},{' '}
                    {pendingCoordinates.lonLat[0].toFixed(6)}
                  </div>
                </div>
              )}

              <div className="form-group">
                <FormControl fullWidth>
                  <InputLabel>Entity model</InputLabel>
                  <Select
                    label="Entity model"
                    value={entityModel?.id || ''}
                    onChange={e => {
                      const model =
                        entityModels.find(
                          m => m.id === Number(e.target.value)
                        ) || null;
                      applyEntityModel(model, { fillName: !editingData });
                    }}
                  >
                    <MenuItem value="">
                      <em>None — pin only</em>
                    </MenuItem>
                    {entityModels.map(model => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name}
                        {!model.pin_icon ? ' (no icon)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {entityModel && !entityModel.pin_icon && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    This entity model has no pin icon. Link an icon on the
                    Entity Models page before placing it on the map.
                  </Typography>
                )}
              </div>

              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={formData.description}
                  onChange={e =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>

              {entityModel &&
                (entityModel.fields || []).map(field => (
                  <div className="form-group" key={field.key}>
                    <label>
                      {field.label}
                      {field.required ? ' *' : ''}
                    </label>
                    {field.enum?.length ? (
                      <select
                        aria-label={field.label}
                        value={entityValues[field.key] ?? ''}
                        required={field.required}
                        onChange={e =>
                          setEntityValues({
                            ...entityValues,
                            [field.key]: e.target.value,
                          })
                        }
                      >
                        <option value="">
                          {field.required ? 'Select a value' : 'None'}
                        </option>
                        {field.enum.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : field.field_type === 'boolean' ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(entityValues[field.key])}
                            onChange={e =>
                              setEntityValues({
                                ...entityValues,
                                [field.key]: e.target.checked,
                              })
                            }
                          />
                        }
                        label={field.label}
                      />
                    ) : field.field_type === 'text' ? (
                      <textarea
                        value={entityValues[field.key] ?? ''}
                        required={field.required}
                        onChange={e =>
                          setEntityValues({
                            ...entityValues,
                            [field.key]: e.target.value,
                          })
                        }
                      />
                    ) : (
                      <input
                        type={
                          field.field_type === 'integer' ||
                          field.field_type === 'float'
                            ? 'number'
                            : 'text'
                        }
                        step={field.field_type === 'float' ? 'any' : undefined}
                        value={entityValues[field.key] ?? ''}
                        required={field.required}
                        onChange={e =>
                          setEntityValues({
                            ...entityValues,
                            [field.key]: e.target.value,
                          })
                        }
                      />
                    )}
                  </div>
                ))}

              <div className="form-actions">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    setShowDataForm(false);
                    setPendingCoordinates(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={
                    !editingData &&
                    Boolean(entityModel) &&
                    !entityModel.pin_icon
                  }
                >
                  {editingData ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapViewer;
